/**
 * Client search on Nautica's dashboard (Traci, Oct 2026: "the same as billing for Kendra"). Type a client's name; their
 * active bookings list below, earliest event first, and each opens that booking's Coordination page (day plan, talent
 * outreach, Box upload links, notes). A client with no active booking shows "No active booking". The bookings load on
 * the first search, from the same endpoint the Bookings tab uses. Rule: clientBookingMatches in bookingsList.js.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/admin/ui/input';
import { clientBookingMatches } from '@/lib/staging/bookingsList';
import { STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch, shortDate } from '../api';

export default function ClientBookingSearch() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChange = (v: string) => {
    setQ(v);
    if (rows || loading || v.trim().length < 2) return;
    setLoading(true);
    stagingFetch('/api/admin/staging/bookings')
      .then((d) => { setRows(d.rows || []); setError(null); })
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  const matches = useMemo(() => (rows ? clientBookingMatches(rows, q) : []), [rows, q]);
  const searching = q.trim().length >= 2;

  return (
    <section aria-label="Client search" className="rounded-xl border bg-card p-4 shadow-sm">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" value={q} onChange={(e) => onChange(e.target.value)} placeholder="Search a client to open their booking…" aria-label="Search clients" />
      </label>
      {searching && (
        <div className="mt-2 text-sm">
          {error && <p className="text-destructive">Could not search: {error}</p>}
          {!error && (loading || !rows) && <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>}
          {!error && rows && matches.length === 0 && <p className="py-2 text-muted-foreground">No active booking</p>}
          {!error && matches.length > 0 && (
            <ul className="max-h-[260px] divide-y overflow-y-auto">
              {matches.map((r: any) => (
                <li key={r.id}>
                  <Link to={`${STAGING_ROOT}/coordination/${r.id}`} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-1 py-2 hover:bg-accent">
                    <span className="font-medium">{r.client_name || 'Client'}</span>
                    <span className="text-xs text-muted-foreground">{[shortDate(r.event_date), r.package, r.location].filter(Boolean).join(' · ')}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
